import {useEffect, useRef, useState} from 'react';
import {Button, Card, Col, Modal, Row, Spinner} from 'react-bootstrap';
import {useDispatch, useSelector} from 'react-redux';
import {saveShippingAddress} from '../../slices/cartSlice';
import {useUpdateActiveOrderMutation} from '../../slices/cartApiSlice';
import {useNavigate} from 'react-router-dom';
import {FaArrowRight, FaCheck, FaEdit, FaMapMarkerAlt, FaPhone, FaPlus, FaShippingFast, FaStore, FaEraser, FaTimes, FaSave} from 'react-icons/fa';
import '../../assets/styles/checkout.mobile.bar.css';
import AddressEditForm from "./AddressEditForm";
import Loader from "../layout/Loader";
import {toast} from "react-toastify";
import useIsMobile from "../../utils/useIsMobile";
import {
    useAddUserAddressMutation,
    useGetAddressesByUserQuery,
    useUpdateUserAddressMutation
} from "../../slices/userApiSlice";

const formatExtraHoursNote = (extraHours) => {
    const hours = Number(extraHours);
    const value = Number.isFinite(hours) ? hours : 0;
    if (value === 1) return '+1 hora adicional';
    return `+${value} horas adicionales`;
};

const ShippingAddressManager = () => {
    const isMobile = useIsMobile();
    const {userInfo} = useSelector((state) => state.auth);
    const { active: activeOrder } = useSelector((state) => state.order);
    const cart = useSelector((state) => state.cart);
    const { selectedCompanyRuc } = cart;
    const { companiesByRuc } = useSelector((state) => state.companies);

    const selectedCompany = companiesByRuc?.[selectedCompanyRuc];
    const selectedCompanyAddress =
        selectedCompany?.location?.address ||
        selectedCompany?.address ||
        '';
    const selectedCompanyState =
        selectedCompany?.state ||
        selectedCompany?.province ||
        selectedCompany?.location?.state ||
        '';
    const selectedCompanyPhone =
        selectedCompany?.phoneNumber ||
        selectedCompany?.phone ||
        selectedCompany?.socialMedia?.whatsapp ||
        process.env.REACT_APP_WHATSAPP_PHONE ||
        '';
    const selectedCompanyGoogleMapsUrl =
        selectedCompany?.location?.googleMapsUrl ||
        selectedCompany?.googleMapsUrl ||
        '';
    const additionalPickupLocations = Array.isArray(selectedCompany?.additionalPickupLocations)
        ? selectedCompany.additionalPickupLocations.filter(
            (location) => location && location.isActive !== false
        )
        : [];

    
    // Prioritize activeOrder.shippingAddress, but fallback to cart.shippingAddress
    // This ensures that if activeOrder is still loading or empty, we use the local selection
    const shippingAddress = activeOrder?.shippingAddress && Object.keys(activeOrder.shippingAddress).length > 0
        ? activeOrder.shippingAddress 
        : cart.shippingAddress;
        
    const {data, refetch, isLoading} = useGetAddressesByUserQuery(userInfo._id);
    const addresses = data?.addresses || [];
    const [addUserAddress, {isLoading: addAddressLoading}] = useAddUserAddressMutation();
    const [updateUserAddress, {isLoading: updateUserLoading}] = useUpdateUserAddressMutation();

    const [editingAddress, setEditingAddress] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isMainButtonVisible, setIsMainButtonVisible] = useState(false);
    const addressFormRef = useRef(null);
    const mainButtonRef = useRef(null);

    useEffect(() => {
        const currentMainButton = mainButtonRef.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsMainButtonVisible(entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        if (currentMainButton) {
            observer.observe(currentMainButton);
        }

        return () => {
            if (currentMainButton) {
                observer.unobserve(currentMainButton);
            }
        };
    }, []);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [updateActiveOrder, { isLoading: isUpdatingOrder }] = useUpdateActiveOrderMutation();

    const isDistributor = userInfo?.priceAssigned === 'pvp_distribuidor';

    const buildPickupAddress = () => ({
        address1: selectedCompanyAddress || 'Dirección no disponible',
        address2: 'NA',
        number: 'SN',
        city: selectedCompany?.city || 'Ciudad no disponible',
        state: selectedCompanyState || selectedCompany?.state || 'Provincia no disponible',
        name: selectedCompany?.tradeName || selectedCompany?.legalName || 'Empresa',
        taxDocId: selectedCompany?.ruc || '',
        type: 'J',
        phoneNumber: selectedCompanyPhone || 'Teléfono no disponible',
        googleMapsUrl: selectedCompanyGoogleMapsUrl,
        label: 'pickup',
        reference: 'Local Comercial',
        extraHours: 0,
        pickupLocationId: null,
    });

    const buildAdditionalPickupAddress = (location) => ({
        address1: location.address || 'Dirección no disponible',
        address2: 'NA',
        number: 'SN',
        city: location.city || 'Ciudad no disponible',
        state:
            location.state ||
            selectedCompany?.state ||
            selectedCompanyState ||
            'Provincia no disponible',
        name:
            location.name || selectedCompany?.tradeName || selectedCompany?.legalName || 'Empresa',
        taxDocId: selectedCompany?.ruc || '',
        type: 'J',
        phoneNumber: location.phoneNumber || selectedCompanyPhone || 'Teléfono no disponible',
        googleMapsUrl: location.googleMapsUrl || '',
        label: 'pickup',
        reference: location.name || 'Punto de retiro',
        extraHours: location.extraHours,
        pickupLocationId: location._id,
    });

    const handleUseAddress = async (address) => {
        // Block distributors from selecting delivery addresses
        if (isDistributor && address.label !== 'pickup') {
            toast.warning('Eres distribuidor con precios mayoristas. Debes recoger en el local o contactar a la empresa para coordinar el envío.');
            return;
        }

        // Optimistically update the UI to show the new selection
        dispatch(saveShippingAddress(address));
        try {
            // This call will update the backend and then refetch the active order
            // because of `invalidatesTags: ['Order']` in the mutation definition.
            await updateActiveOrder({
                shippingAddress: address,
                companyId: selectedCompanyRuc, // Pass the selected company ID
                fastPricing: false
            }).unwrap();
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    const handleContinue = async () => {
        // Check if we have a valid shipping address
        // It can be a pickup (label === 'pickup') or a regular address (has _id or address1)
        const isValidAddress = shippingAddress && (
            shippingAddress.label === 'pickup' || 
            shippingAddress._id || 
            (shippingAddress.address1 && shippingAddress.city)
        );

        if (isValidAddress) {
            try {
                await updateActiveOrder({
                    shippingAddress,
                    companyId: selectedCompanyRuc,
                    fastPricing: false
                }).unwrap();
                navigate('/placeorder');
            } catch (err) {
                toast.error(err?.data?.message || err.error);
            }
        } else {
            toast.error('Debe seleccionar una opción');
        }
    };

    const isMainPickupSelected =
        shippingAddress?.label === 'pickup' && !shippingAddress?.pickupLocationId;

    const isAdditionalPickupSelected = (locationId) => {
        if (!locationId || shippingAddress?.label !== 'pickup' || !shippingAddress?.pickupLocationId) {
            return false;
        }
        return String(shippingAddress.pickupLocationId) === String(locationId);
    };

    const isSelected = (addr) => {
        if (!shippingAddress) return false;
        if (addr.label === 'pickup' && shippingAddress.label === 'pickup') {
            const addrPickupId = addr.pickupLocationId ? String(addr.pickupLocationId) : null;
            const selectedPickupId = shippingAddress.pickupLocationId
                ? String(shippingAddress.pickupLocationId)
                : null;
            return addrPickupId === selectedPickupId;
        }
        // Compare by _id if available, otherwise compare content
        if (addr._id && shippingAddress._id) {
            return addr._id === shippingAddress._id;
        }
        // Fallback for addresses without IDs (e.g. newly created or legacy)
        return addr.address1 === shippingAddress.address1 &&
               addr.city === shippingAddress.city &&
               addr.name === shippingAddress.name;
    };

    const handleSubmit = async (formData) => {
        try {
            if (editingAddress) {
                await updateUserAddress({
                    userId: userInfo._id,
                    addressId: editingAddress._id,
                    body: formData,
                }).unwrap();
            } else {
                await addUserAddress({
                    userId: userInfo._id,
                    address: formData,
                }).unwrap();
            }
            setEditingAddress(null);
            setShowModal(false);
            refetch();
            toast.success(`Dirección ${editingAddress ? 'actualizada' : 'agregada'} correctamente`);
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    return (
        <div className="shipping-manager-container mb-5">
            {/* Distributor Notice */}
            {isDistributor && (
                <div className="mb-3">
                    <div
                        className="alert alert-info border-0 shadow-sm d-flex align-items-start gap-3 mb-0"
                        style={{
                            backgroundColor: '#e7f3ff',
                            borderLeft: '4px solid #0d6efd'
                        }}
                    >
                        <div className="flex-shrink-0 mt-1">
                            <FaStore className="text-primary" style={{fontSize: '1.5rem'}} />
                        </div>
                        <div className="flex-grow-1">
                            <h6 className="fw-bold mb-2 text-primary">
                                <FaShippingFast className="me-2" />
                                Cuenta Distribuidor - Precios Mayoristas
                            </h6>
                            <p className="mb-0 small" style={{lineHeight: '1.5'}}>
                                Tienes precios mayoristas. Debes <strong>recoger en el local</strong> ó Si necesitas coordinar el envío, usa el <strong>botón de ayuda</strong> disponible después de la cotización.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <section className="shipping-choice-group shipping-choice-group--pickup" aria-labelledby="pickup-options-title">
                <div className="shipping-choice-heading">
                    <span className="shipping-choice-icon" aria-hidden="true">
                        <FaStore />
                    </span>
                    <div>
                        <span className="shipping-choice-kicker">Retiro</span>
                        <h2 id="pickup-options-title">Recoger en tienda</h2>
                        <p>Elige un local y pasa a retirar. Estas opciones no tienen costo de envío.</p>
                    </div>
                </div>
            <Row className="g-3 g-md-4">
                {/* Dynamic pick up option */}
                <Col xs={12} md={6} lg={4}>
                    <Card
                        className={`h-100 border-2 transition-all cursor-pointer address-card address-card--pickup ${
                            isMainPickupSelected ? 'is-selected' : 'border-light shadow-sm'
                        }`}
                        onClick={() => {
                            if (!selectedCompany) {
                                toast.error("Información de la empresa no disponible");
                                return;
                            }
                            handleUseAddress(buildPickupAddress());
                        }}
                    >
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="icon-circle text-white" style={{backgroundColor: '#b5430f'}}>
                                    <FaStore/>
                                </div>
                                {isUpdatingOrder && isMainPickupSelected ? (
                                    <Spinner animation="border" size="sm" variant="primary" />
                                ) : (
                                    isMainPickupSelected && (
                                        <span className="badge rounded-pill" style={{backgroundColor: '#b5430f'}}>
                                            <FaCheck className="me-1"/> Seleccionado
                                        </span>
                                    )
                                )}
                            </div>
                            <span className="shipping-choice-chip shipping-choice-chip--pickup mb-2">Retiro en tienda</span>
                            <Card.Title className="h5 mb-2">Recoger en el Local</Card.Title>
                            <Card.Text className="text-muted small mb-0 flex-grow-1">
                                <strong>{selectedCompany?.city || 'Ciudad'}, {selectedCompanyState || 'Provincia'}</strong><br/>
                                <FaMapMarkerAlt className="me-2"/>
                                {selectedCompanyAddress || 'Dirección no disponible'}<br/>
                                <FaPhone className="me-2"/>
                                {selectedCompanyPhone || 'Teléfono no disponible'}<br/>
                                <strong>{selectedCompany?.tradeName || selectedCompany?.legalName || 'Empresa'}</strong><br/>
                                <span className="d-inline-block mt-2 fw-semibold" style={{color: '#b5430f'}}>
                                    Disponible de inmediato
                                </span>
                            </Card.Text>
                            <div className="mt-3">
                                <Button
                                    type="button"
                                    variant={isMainPickupSelected ? 'primary' : 'outline-primary'}
                                    className="w-100 rounded-pill pickup-action"
                                >
                                    <strong>{isMainPickupSelected ? 'Seleccionado' : 'Retirar aquí'}</strong>
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {additionalPickupLocations.map((location, index) => {
                    const locationSelected = isAdditionalPickupSelected(location._id);
                    const locationName = location.name || 'Punto de retiro';
                    const locationState = location.state || selectedCompanyState || 'Provincia';
                    const locationPhone = location.phoneNumber || selectedCompanyPhone || 'Teléfono no disponible';

                    return (
                        <Col xs={12} md={6} lg={4} key={location._id || `${location.address || 'pickup'}-${index}`}>
                            <Card
                                className={`h-100 border-2 transition-all cursor-pointer address-card address-card--pickup ${
                                    locationSelected ? 'is-selected' : 'border-light shadow-sm'
                                }`}
                                onClick={() => {
                                    if (!selectedCompany) {
                                        toast.error("Información de la empresa no disponible");
                                        return;
                                    }
                                    handleUseAddress(buildAdditionalPickupAddress(location));
                                }}
                            >
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="icon-circle text-white" style={{backgroundColor: '#b5430f'}}>
                                            <FaStore/>
                                        </div>
                                        {isUpdatingOrder && locationSelected ? (
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        ) : (
                                            locationSelected && (
                                                <span className="badge rounded-pill" style={{backgroundColor: '#b5430f'}}>
                                                    <FaCheck className="me-1"/> Seleccionado
                                                </span>
                                            )
                                        )}
                                    </div>
                                    <span className="shipping-choice-chip shipping-choice-chip--pickup mb-2">Punto de retiro</span>
                                    <Card.Title className="h5 mb-2">{locationName}</Card.Title>
                                    <Card.Text className="text-muted small mb-0 flex-grow-1">
                                        <strong>{location.city || 'Ciudad'}, {locationState}</strong><br/>
                                        <FaMapMarkerAlt className="me-2"/>
                                        {location.address || 'Dirección no disponible'}<br/>
                                        <FaPhone className="me-2"/>
                                        {locationPhone}<br/>
                                        <strong>{location.name || selectedCompany?.tradeName || selectedCompany?.legalName || 'Empresa'}</strong><br/>
                                        <span className="d-inline-block mt-2 fw-semibold" style={{color: '#b5430f'}}>
                                            {formatExtraHoursNote(location.extraHours)}
                                        </span>
                                    </Card.Text>
                                    <div className="mt-3">
                                        <Button
                                            type="button"
                                            variant={locationSelected ? 'primary' : 'outline-primary'}
                                            className="w-100 rounded-pill pickup-action"
                                        >
                                            <strong>{locationSelected ? 'Seleccionado' : 'Retirar aquí'}</strong>
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    );
                })}

            </Row>
            </section>

            <section className="shipping-choice-group shipping-choice-group--delivery" aria-labelledby="delivery-options-title">
                <div className="shipping-choice-heading">
                    <span className="shipping-choice-icon" aria-hidden="true">
                        <FaShippingFast />
                    </span>
                    <div>
                        <span className="shipping-choice-kicker">Envío</span>
                        <h2 id="delivery-options-title">Enviar a mi dirección</h2>
                        <p>
                            {isDistributor
                                ? 'Con precios mayoristas solo puedes retirar en tienda. Tus direcciones quedan visibles, pero no se pueden elegir.'
                                : 'Estas son tus direcciones. El pedido llega al lugar que elijas.'}
                        </p>
                    </div>
                </div>
            <Row className="g-3 g-md-4">
                {isLoading && (
                    <Col xs={12} className="text-center py-4">
                        <Loader/>
                    </Col>
                )}

                {addresses.map((addr) => (
                    <Col xs={12} md={6} lg={4} key={addr._id}>
                        <Card
                            className={`h-100 border-2 transition-all address-card address-card--delivery ${
                                isSelected(addr) ? 'is-selected' : 'border-light shadow-sm'
                            } ${isDistributor ? 'opacity-50' : 'cursor-pointer'}`}
                            onClick={() => !isDistributor && handleUseAddress(addr)}
                            style={isDistributor ? {pointerEvents: 'none', cursor: 'not-allowed'} : {}}
                        >
                            <Card.Body className="d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="icon-circle bg-primary text-white">
                                        <FaShippingFast/>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        {isUpdatingOrder && isSelected(addr) ? (
                                            <Spinner animation="border" size="sm" variant="primary" />
                                        ) : (
                                            isSelected(addr) && (
                                                <span className="badge bg-primary rounded-pill">
                                                    <FaCheck className="me-1"/> Seleccionado
                                                </span>
                                            )
                                        )}
                                        {!isDistributor && (
                                            <Button
                                                variant="light"
                                                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                                style={{width: '38px', height: '38px'}}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingAddress(addr);
                                                    setShowModal(true);
                                                }}
                                                title="Editar dirección"
                                            >
                                                <FaEdit/>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <span className="shipping-choice-chip shipping-choice-chip--delivery mb-2">Mi dirección</span>
                                <Card.Title className="h5 mb-2 text-truncate">
                                    {addr.city}, {addr.state}
                                </Card.Title>
                                <div className="text-muted small mb-0 flex-grow-1">
                                    <div className="mb-1 text-dark fw-semibold">
                                        <FaMapMarkerAlt className="me-2"/>
                                        {addr.address1} {addr.address2 && `& ${addr.address2}`}
                                    </div>
                                    <div className="mb-1">
                                        <strong>Recibe:</strong> {addr.name}
                                    </div>
                                    <div className="mb-1">
                                        <FaPhone className="me-2"/>
                                        {addr.phoneNumber}
                                    </div>
                                </div>
                                <div className="mt-3 d-flex gap-2">
                                    <Button
                                        variant={isSelected(addr) ? 'primary' : 'outline-primary'}
                                        className="flex-grow-1 rounded-pill"
                                    >
                                        <strong>{isSelected(addr) ? 'Seleccionado' : 'Enviar aquí'}</strong>
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}

                {/* Add new address card */}
                {!isDistributor && (
                    <Col xs={12} md={6} lg={4}>
                        <Card
                            className="h-100 border-2 border-dashed border-muted bg-light cursor-pointer transition-all add-address-card"
                            onClick={() => {
                                setEditingAddress(null);
                                setShowModal(true);
                            }}
                        >
                            <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                                <div className="icon-circle bg-secondary text-white mb-3">
                                    <FaPlus/>
                                </div>
                                <h5 className="mb-0">Agregar Nueva Dirección</h5>
                            </Card.Body>
                        </Card>
                    </Col>
                )}
            </Row>
            </section>

            <div ref={mainButtonRef} className="d-flex justify-content-center mt-5 mb-5">
                <Button
                    variant="primary"
                    size="lg"
                    className="px-5 py-3 rounded-pill shadow-lg d-flex align-items-center gap-3"
                    onClick={handleContinue}
                    disabled={isUpdatingOrder}
                >
                    {isUpdatingOrder ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <>
                            <span className="fw-bold h4 mb-0 text-uppercase letter-spacing-1">{userInfo?.priceAssigned === 'pvp_distribuidor' ? 'Cotizar' : 'Ir a pagar'}</span>
                            <FaCheck size={24} />
                        </>
                    )}
                </Button>
            </div>

            {isMobile && !isMainButtonVisible && (
                <>
                    <div className="checkout-mobile-bar-spacer" aria-hidden="true" />
                    <div className="checkout-mobile-bar">
                        <Button
                            type="button"
                            className="checkout-mobile-bar-action"
                            onClick={handleContinue}
                            disabled={isUpdatingOrder}
                        >
                            {isUpdatingOrder ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <>
                                    {userInfo?.priceAssigned === 'pvp_distribuidor' ? 'Cotizar' : 'Ir a pagar'}
                                    <FaArrowRight />
                                </>
                            )}
                        </Button>
                    </div>
                </>
            )}

            {(addAddressLoading || updateUserLoading) && (
                <div className="text-center mt-3">
                    <Loader/>
                </div>
            )}

            <Modal
                show={showModal}
                onHide={() => {setShowModal(false)}}
                centered
                size="lg"
                contentClassName="border-0 shadow-lg"
            >
                <Modal.Header closeButton >
                    <Modal.Title className="fw-bold">
                        {editingAddress
                            ? <><FaEdit className="me-2"/>Editar Dirección</>
                            : <><FaPlus className="me-2"/>Nueva Dirección</>
                        }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light p-0">
                    <AddressEditForm
                        ref={addressFormRef}
                        userInfo={userInfo}
                        initialData={editingAddress || {}}
                        onSubmit={handleSubmit}
                        onCancel={() => setShowModal(false)}
                    />
                </Modal.Body>
                <Modal.Footer className="bg-white p-3 border-top">
                    <Row className="w-100 g-2 justify-content-md-end">
                        <Col xs={6} md="auto" className="order-2 order-md-1">
                            <Button
                                variant="outline-secondary"
                                onClick={() => addressFormRef.current?.wipeForm()}
                                className="w-100 d-flex align-items-center justify-content-center"
                            >
                                <FaEraser className="me-2" /> Limpiar
                            </Button>
                        </Col>
                        <Col xs={6} md="auto" className="order-3 order-md-2">
                            <Button
                                variant="outline-danger"
                                onClick={() => setShowModal(false)}
                                className="w-100 d-flex align-items-center justify-content-center"
                            >
                                <FaTimes className="me-2" /> Cancelar
                            </Button>
                        </Col>
                        <Col xs={12} md="auto" className="order-1 order-md-3">
                            <Button
                                type="submit"
                                form="address-edit-form"
                                variant="primary"
                                className="w-100 px-5 shadow-sm d-flex align-items-center justify-content-center"
                            >
                                <FaSave className="me-2" /> <strong>Guardar Dirección</strong>
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ShippingAddressManager;